import { Color, ConeGeometry, Float32BufferAttribute, Group, MeshLambertMaterial, NearestFilter } from "three"
import type { NeighborElevationCollection } from "../../../../../rx/memo/useNodeNeighborBaseFloorElevationReactiveMap"
import type { EntityGraphNode } from "../../../../core/engine/EntityGraphNode/EntityGraphNode.type"
import type { ForestEntity } from "../../../../core/entity/EntityType/Forest"
import tmpRngWith from "../../../../core/rng/tmpRngWith"
import { VERTICES_PER_TILE } from "../../../consts"
import createBatchedMeshWithGeometryList from "../../../fn/createBatchedMeshWithGeometryList"
import createCamoMapTexture from "../../../fn/createCamoMapTexture"
import createSingleColorRGBFloat32Array from "../../../fn/createSingleColorRGBFloat32Array"
import enableShadows from "../../../fn/enableShadows"
import { dummyMatrix, dummyObject3D, dummyPosition, dummyRotation, dummyScale, resetDummyObject3D } from "../../DummyObject3D/dummyObject3D"
import generateTreePositionsV3 from "./generateTreePositionsV3"

// // const lowResForestNoise = createCamoMapTexture(tmpRngWith(2), 16, 8)
// const mediumBarkColor = new Color(0x5D4037) // Medium brown bark

// const w = 0.5 * (VERTICES_PER_TILE / 32)
// const h = 4 * (VERTICES_PER_TILE / 32)
// const geometry = new ConeGeometry(w, h, 6, 1)
// // mesh.position.y += (h / 2)

// const darkGreenLeafColor = new Color(0x1B5E20) // Dark green foliage
// const w = 2 * (VERTICES_PER_TILE / 32)
// const h = 5 * (VERTICES_PER_TILE / 32)
// const geometry = new ConeGeometry(w, h, 7, 1)

const lowResForestNoise = createCamoMapTexture(tmpRngWith(2), 8, 8)
lowResForestNoise.minFilter = NearestFilter

export default async function createForestTerrainClusterGroupV2Simple(
  list: Array<EntityGraphNode<ForestEntity>>,
  neighborBaseElevationReactiveMap: Map<number, NeighborElevationCollection>,
): Promise<Group> {
  const group = new Group()
  group.name = 'ForestTerrainCluster'
  
  try {
    // const treeVariants = await loadAndMergeForestTreeGeometry()
    // if (treeVariants.length === 0) {
    //   console.error('Failed to load tree geometries')
    //   return group
    // }

    const w0 = 0.6 * (VERTICES_PER_TILE / 32)
    const h0 = 5 * (VERTICES_PER_TILE / 32)
    const treeTrunkGeometry = (new ConeGeometry(w0, h0, 10, 3)).toNonIndexed()
    treeTrunkGeometry.setAttribute('color', new Float32BufferAttribute(createSingleColorRGBFloat32Array(new Color(0.5, 0.5, 0.5), treeTrunkGeometry.attributes.position.count), 3))

    const darkGreenLeafColor = new Color(0x1B5E20) // Dark green foliage
    const w = 3 * (VERTICES_PER_TILE / 32)
    const h = 8 * (VERTICES_PER_TILE / 32)
    const treeLeafGeometry = (new ConeGeometry(w, h, 12, 3)).toNonIndexed()
    treeLeafGeometry.setAttribute('color', new Float32BufferAttribute(createSingleColorRGBFloat32Array(darkGreenLeafColor, treeLeafGeometry.attributes.position.count), 3))

    const treeVariants = [treeTrunkGeometry, treeLeafGeometry]

    const material = new MeshLambertMaterial({
      vertexColors: true,
      map: lowResForestNoise,
    })

    const variantsCount = 1
    const positions = generateTreePositionsV3(list, variantsCount, neighborBaseElevationReactiveMap)

    // Create BatchedMesh with initial capacity
    const batchSize = positions.length * 2
    const batchedMesh = createBatchedMeshWithGeometryList(batchSize, treeVariants, material)
    
    // Add all tree variant geometries to the batched mesh
    const trunkGeometryId = batchedMesh.addGeometry(treeTrunkGeometry)
    const leafGeometryId = batchedMesh.addGeometry(treeLeafGeometry)
    
    resetDummyObject3D()
    dummyScale.multiplyScalar(0.5)

    // const instanceIds: number[] = []
    
    // // Add instances with their respective variant geometries
    // for (let i = 0; i < positions.length; i++) {
    //   const { v: variantIndex } = positions[i]
    //   const geometryId = geometryIds[variantIndex]
    //   const instanceId = batchedMesh.addInstance(geometryId)
    //   instanceIds.push(instanceId)
    // }
    // Set matrices for all instances
    for (let i = 0; i < positions.length; i++) {
      const trunkInstanceId = batchedMesh.addInstance(trunkGeometryId)
      const { x, y, r, h } = positions[i]
      dummyPosition.x = x
      dummyPosition.z = y
      dummyPosition.y = h + 0.3
      dummyRotation.y = r
      dummyObject3D.updateMatrix()
      batchedMesh.setMatrixAt(trunkInstanceId, dummyMatrix)

      const leafInstanceId = batchedMesh.addInstance(leafGeometryId)
      dummyPosition.x = x
      dummyPosition.z = y
      dummyPosition.y = h + 0.85
      dummyRotation.y = r
      dummyObject3D.updateMatrix()
      batchedMesh.setMatrixAt(leafInstanceId, dummyMatrix)
    }

    enableShadows(batchedMesh)
    group.add(batchedMesh)
    
    return group
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    console.error('Error creating Forest terrain cluster:', errorMessage)
    // Return the group even if there was an error to avoid breaking the app
    return group
  }
}
